import React, { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

interface TatianaProps {
  position?: [number, number, number]
  scale?: number
  isTyping?: boolean
  isProcessing?: boolean
}

export const Tatiana: React.FC<TatianaProps> = ({
  position = [-3, 0, -5],
  scale = 1,
  isTyping = false,
  isProcessing = false
}) => {
  const groupRef = useRef<THREE.Group>(null)
  const monolithRef = useRef<THREE.Mesh>(null)
  const particleRingRef = useRef<THREE.Points>(null)
  const innerGlowRef = useRef<THREE.Mesh>(null)
  const rimLightRef = useRef<THREE.PointLight>(null)
  
  // Create elongated octahedron (obsidian shard)
  const monolithGeometry = useMemo(() => {
    // Create a custom elongated octahedron geometry
    const geometry = new THREE.OctahedronGeometry(0.4, 0)
    const positions = geometry.attributes.position.array as Float32Array
    
    // Elongate along Y axis to create shard shape
    for (let i = 0; i < positions.length; i += 3) {
      positions[i + 1] *= 2.5 // Stretch Y axis
    }
    
    geometry.computeVertexNormals()
    return geometry
  }, [])
  
  // Particle ring geometry - orbiting microscopic cyan particles
  const [ringGeometry, ringMaterial] = useMemo(() => {
    const particleCount = 300
    const positions = new Float32Array(particleCount * 3)
    const colors = new Float32Array(particleCount * 3)
    const phases = new Float32Array(particleCount) // For orbital animation
    
    for (let i = 0; i < particleCount; i++) {
      // Create ring distribution
      const angle = (i / particleCount) * Math.PI * 2
      const radius = 0.8 + Math.random() * 0.4
      const heightVariation = (Math.random() - 0.5) * 0.3
      
      positions[i * 3] = Math.cos(angle) * radius
      positions[i * 3 + 1] = heightVariation
      positions[i * 3 + 2] = Math.sin(angle) * radius
      
      // Intense cyan color
      colors[i * 3] = 0
      colors[i * 3 + 1] = 0.9 + Math.random() * 0.1
      colors[i * 3 + 2] = 1
      
      phases[i] = Math.random() * Math.PI * 2
    }
    
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geo.setAttribute('phase', new THREE.BufferAttribute(phases, 1))
    
    const mat = new THREE.PointsMaterial({
      size: 0.04,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    })
    
    return [geo, mat]
  }, [])
  
  // Inner glow core
  const glowGeometry = useMemo(() => {
    return new THREE.OctahedronGeometry(0.15, 1)
  }, [])
  
  // Animation loop
  useFrame((state) => {
    const time = state.clock.getElapsedTime()
    
    // Animation parameters based on processing state
    const pulseIntensity = isProcessing ? 1.8 : (isTyping ? 1.2 : 1.0)
    const pulseCycle = isProcessing ? 1.5 : 4.0 // 1.5s for processing, 4s for normal
    const pulseSpeed = (2 * Math.PI) / pulseCycle
    const particleOrbitSpeed = isProcessing ? 0.8 : 0.3
    
    if (groupRef.current) {
      // Gentle floating motion (sinusoidal y-axis drift)
      groupRef.current.position.y = position[1] + Math.sin(time * 0.8) * 0.08
      
      // Slow rotation
      groupRef.current.rotation.y = time * 0.1
      groupRef.current.rotation.z = Math.sin(time * 0.2) * 0.03
    }
    
    if (monolithRef.current) {
      // Pulse on the monolith based on processing state
      const pulse = 1 + Math.sin(time * pulseSpeed) * 0.03 * pulseIntensity
      monolithRef.current.scale.setScalar(pulse)
    }
    
    if (particleRingRef.current) {
      // Orbit the particle ring - faster when processing
      particleRingRef.current.rotation.y = time * particleOrbitSpeed
      particleRingRef.current.rotation.x = Math.sin(time * 0.1) * 0.1
      
      // Pulse particle opacity
      const material = particleRingRef.current.material as THREE.PointsMaterial
      const baseOpacity = isProcessing ? 0.9 : 0.7
      const opacityPulse = isProcessing ? 0.3 : 0.2
      material.opacity = baseOpacity + Math.sin(time * pulseSpeed) * opacityPulse
    }
    
    if (innerGlowRef.current) {
      // Pulsing emissive core - more intense when processing
      const material = innerGlowRef.current.material as THREE.MeshBasicMaterial
      const baseOpacity = isProcessing ? 0.95 : (isTyping ? 0.8 : 0.5)
      const opacityPulse = isProcessing ? 0.3 : 0.2
      material.opacity = baseOpacity + Math.sin(time * pulseSpeed) * opacityPulse
      
      // Increase emissive intensity when processing
      if (isProcessing) {
        material.color.setHex(0x00FFFF)
      }
    }
    
    if (rimLightRef.current) {
      // Pulsing rim light - more intense when processing
      const baseIntensity = isProcessing ? 4.5 : (isTyping ? 3 : 1.5)
      const intensityPulse = isProcessing ? 1.0 : 0.5
      rimLightRef.current.intensity = baseIntensity + Math.sin(time * pulseSpeed) * intensityPulse
    }
  })
  
  return (
    <group ref={groupRef} position={position} scale={scale}>
      {/* Rim light for dramatic effect */}
      <pointLight
        ref={rimLightRef}
        color={isProcessing ? 0x00FFFF : 0x00FFFF}
        intensity={isProcessing ? 4.5 : 1.5}
        distance={8}
        position={[0.5, 0.5, 0.5]}
      />
      
      {/* Secondary purple accent light */}
      <pointLight
        color={0x8A2BE2}
        intensity={isProcessing ? 1.2 : 0.8}
        distance={5}
        position={[-0.3, -0.5, -0.3]}
      />
      
      {/* The Obsidian Monolith - Main body */}
      <mesh ref={monolithRef} geometry={monolithGeometry}>
        <meshStandardMaterial
          color={0x1a1a2e}
          metalness={0.9}
          roughness={0.3}
          envMapIntensity={1.2}
        />
      </mesh>
      
      {/* Emissive edges overlay - brighter when processing */}
      <mesh geometry={monolithGeometry} scale={1.02}>
        <meshBasicMaterial
          color={0x00FFFF}
          transparent={true}
          opacity={isProcessing ? 0.2 : 0.1}
          wireframe={true}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      
      {/* Inner glowing core */}
      <mesh ref={innerGlowRef} geometry={glowGeometry}>
        <meshBasicMaterial
          color={0x00FFFF}
          transparent={true}
          opacity={isProcessing ? 0.95 : 0.5}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      
      {/* Particle ring - orbiting cyan particles */}
      <points ref={particleRingRef} geometry={ringGeometry} material={ringMaterial} />
      
      {/* Base glow platform - brighter when processing */}
      <mesh position={[0, -1.2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.3, 0.6, 32]} />
        <meshBasicMaterial
          color={0x00FFFF}
          transparent={true}
          opacity={isProcessing ? 0.3 : 0.15}
          blending={THREE.AdditiveBlending}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  )
}

export default Tatiana
